import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarX, Car, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtDateTime, fmtDateTimeZone, fmtMoney, setPageTitle } from "../lib/format";

export default function HostBookings() {
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState(null);
  const [otpFor, setOtpFor] = useState(null);
  const [otpVal, setOtpVal] = useState("");
  const [vehConfirmed, setVehConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setError(null);
    api.hostBookings().then(setBookings).catch((e) => { setError(e.message); setBookings([]); });
  };
  useEffect(() => { setPageTitle("Host Bookings"); load(); }, []);

  const closeModal = () => { setOtpFor(null); setOtpVal(""); setVehConfirmed(false); };

  const verify = async (e) => {
    e.preventDefault();
    if (!otpFor || otpVal.trim().length !== 4 || !vehConfirmed) return;
    setBusy(true);
    try {
      await api.verifyOtp(otpFor.booking_id, otpVal.trim(), true);
      toast.success("Vehicle confirmed — OTP verified, payment captured");
      closeModal();
      load();
    } catch (err) {
      toast.error(err.message);
      if (err.status === 429) { closeModal(); load(); }
    } finally { setBusy(false); }
  };

  const mismatch = async () => {
    if (!otpFor) return;
    setBusy(true);
    try {
      await api.reportVehicleMismatch(otpFor.booking_id);
      toast.info("Mismatch reported — session not started, no charge. Support will follow up.");
      closeModal();
      load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const vs = otpFor?.vehicle_snapshot;

  return (
    <div className="min-h-screen flex flex-col" data-testid="host-bookings-page">
      <Navbar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Host</p>
        <h1 className="type-display mt-3">Bookings on your spaces</h1>
        <p className="mt-1 text-sm text-zinc-400">Verify the arriving vehicle matches the booking, then enter the driver's 4-digit arrival code to release payment.</p>
        <div className="mt-8">
          {error ? <ErrorState body={error} onRetry={load} /> : bookings === null ? <PageLoading label="Loading bookings…" /> :
            bookings.length === 0 ? (
              <EmptyState icon={<CalendarX className="w-7 h-7 text-[#FF3B45]" />} title="No bookings yet"
                body="When drivers book your spaces, they'll appear here with their verification status."
                action={<Link to="/host/listings/new" data-testid="host-bookings-create" className="btn-primary text-sm">Create a listing</Link>} />
            ) : (
              <div className="space-y-3" data-testid="host-bookings-list">
                {bookings.map((b) => (
                  <div key={b.booking_id} className="card-dark p-4 sm:p-5" data-testid={`host-booking-${b.booking_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-bold truncate">{b.spot_title}</p>
                        <p className="text-xs text-zinc-400 mt-1">{fmtDateTimeZone(b.start_time, b.timezone)} → {fmtDateTimeZone(b.end_time, b.timezone)}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Driver: {b.driver_name || b.driver_email || "—"}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-display font-bold">{fmtMoney(b.host_payout)}</span>
                        <StatusBadge value={b.status} />
                        {b.status === "authorized" && (
                          <button onClick={() => setOtpFor(b)} data-testid={`verify-otp-open-${b.booking_id}`} className="btn-primary !px-4 !py-2 text-xs">
                            <KeyRound className="w-3.5 h-3.5" /> Verify arrival
                          </button>
                        )}
                        <Link to={`/bookings/${b.booking_id}`} data-testid={`host-booking-open-${b.booking_id}`} className="btn-secondary !px-4 !py-2 text-xs">Details</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </main>
      {otpFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Verify guest arrival">
          <form onSubmit={verify} className="card-dark p-8 w-full max-w-sm text-center" data-testid="otp-verify-modal">
            <KeyRound className="w-8 h-8 text-[#FF3B45] mx-auto" />
            <h2 className="mt-4 font-bold text-lg">Verify guest arrival</h2>
            <p className="mt-1 text-xs text-zinc-400">For <span className="text-zinc-200">{otpFor.spot_title}</span> — confirm the vehicle first, then enter the driver's code.</p>

            <div className="mt-5 p-4 rounded-xl bg-[#0D1114]/70 border border-[#27272A] text-left" data-testid="otp-vehicle-card">
              <p className="meta-label flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Vehicle on this booking</p>
              {vs ? (
                <div className="mt-2.5">
                  <p className="font-mono font-bold text-lg tracking-widest" data-testid="otp-vehicle-plate">{vs.plate || "—"}</p>
                  <p className="text-xs text-zinc-400 mt-1" data-testid="otp-vehicle-desc">
                    {[vs.nickname, [vs.make, vs.model].filter(Boolean).join(" "), vs.colour].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
              ) : (
                <p className="mt-2.5 text-xs text-amber-400" data-testid="otp-vehicle-none">No vehicle recorded on this booking (legacy) — verify the guest's booking in their app before continuing.</p>
              )}
              <label className="mt-3.5 flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={vehConfirmed} onChange={(e) => setVehConfirmed(e.target.checked)}
                       data-testid="otp-vehicle-confirm" className="mt-0.5 accent-[#E50914]" />
                <span className="text-sm text-zinc-300">The arriving vehicle matches the license plate shown above</span>
              </label>
              <button type="button" onClick={mismatch} disabled={busy} data-testid="otp-vehicle-mismatch"
                      className="mt-3 w-full text-xs font-semibold px-3 py-2 rounded-full border border-[#27272A] text-amber-400 hover:border-amber-500 transition-colors inline-flex items-center justify-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> Vehicle doesn't match — report
              </button>
            </div>

            <input data-testid="otp-input" value={otpVal} onChange={(e) => setOtpVal(e.target.value.replace(/\D/g, "").slice(0, 4))}
                   inputMode="numeric" autoComplete="one-time-code" aria-label="4-digit code" disabled={!vehConfirmed}
                   className="input-dark mt-5 text-center font-mono-otp !text-2xl tracking-[0.5em] disabled:opacity-40" placeholder="••••" />
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={closeModal} data-testid="otp-cancel" className="btn-secondary flex-1 text-sm">Cancel</button>
              <button type="submit" disabled={busy || otpVal.length !== 4 || !vehConfirmed} data-testid="otp-verify-submit" className="btn-primary flex-1 text-sm disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify
              </button>
            </div>
            <p className="mt-3 text-[11px] text-zinc-500">5 failed attempts cancels the booking and refunds the driver.</p>
          </form>
        </div>
      )}
      <Footer />
    </div>
  );
}
